"use client";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Project } from "@/data/projects";
import {
  fadeInUp,
  staggerContainer,
  scaleOnHover,
  slideInLeft,
  fadeInScale,
} from "../animations";

interface ProjectTechnicalProps {
  project: Project;
}

export const ProjectTechnical: React.FC<ProjectTechnicalProps> = ({
  project,
}) => {
  // Count the number of technical sections
  const sectionCount = [
    project.technicalDetails.frontend,
    project.technicalDetails.backend,
    project.technicalDetails.infrastructure,
    project.technicalDetails.admin,
  ].filter(Boolean).length;

  // Determine grid columns based on section count
  const gridCols = sectionCount % 2 === 0 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <>
      <motion.div {...fadeInUp} className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-6">
          Technical Architecture
        </h2>
        <motion.div
          className="bg-white/5 p-6 rounded-lg mb-6"
          whileHover={scaleOnHover.hover}
          transition={scaleOnHover.transition}
        >
          <p className="text-gray-300 mb-4">{project.architecture.overview}</p>
          <div className="grid md:grid-cols-2 gap-4">
            {project.architecture.keyComponents.map((component, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-2"
                {...slideInLeft}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-pink-500">•</span>
                <span className="text-gray-300">{component}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
        {project.architecture.diagram && (
          <motion.div className="relative h-64 md:h-96" {...fadeInScale}>
            <Image
              src={project.architecture.diagram}
              alt="Architecture diagram"
              fill
              className="object-contain"
            />
          </motion.div>
        )}
      </motion.div>

      <motion.div {...fadeInUp} className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-6">
          Technical Details
        </h2>
        <motion.div
          className={`grid ${gridCols} gap-6`}
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {project.technicalDetails.frontend && (
            <motion.div
              className="bg-white/5 p-6 rounded-lg"
              variants={fadeInUp}
              whileHover={scaleOnHover.hover}
              transition={scaleOnHover.transition}
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Frontend
              </h3>
              <div className="space-y-2">
                <p className="text-gray-300">
                  <span className="text-pink-500">Framework:</span>{" "}
                  {project.technicalDetails.frontend.framework}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">State Management:</span>{" "}
                  {project.technicalDetails.frontend.stateManagement}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">Styling:</span>{" "}
                  {project.technicalDetails.frontend.styling}
                </p>
              </div>
              <h4 className="text-lg font-semibold text-white mt-4 mb-2">
                Notable Features
              </h4>
              <motion.ul
                className="list-disc list-inside text-gray-300 space-y-1"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {project.technicalDetails.frontend.notableFeatures.map(
                  (feature, index) => (
                    <motion.li key={index} variants={fadeInUp}>
                      {feature}
                    </motion.li>
                  )
                )}
              </motion.ul>
            </motion.div>
          )}

          {project.technicalDetails.backend && (
            <motion.div
              className="bg-white/5 p-6 rounded-lg"
              variants={fadeInUp}
              whileHover={scaleOnHover.hover}
              transition={scaleOnHover.transition}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Backend</h3>
              <div className="space-y-2">
                <p className="text-gray-300">
                  <span className="text-pink-500">Framework:</span>{" "}
                  {project.technicalDetails.backend.framework}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">Database:</span>{" "}
                  {project.technicalDetails.backend.database}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">API:</span>{" "}
                  {project.technicalDetails.backend.api}
                </p>
              </div>
              <h4 className="text-lg font-semibold text-white mt-4 mb-2">
                Notable Features
              </h4>
              <motion.ul
                className="list-disc list-inside text-gray-300 space-y-1"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {project.technicalDetails.backend.notableFeatures.map(
                  (feature, index) => (
                    <motion.li key={index} variants={fadeInUp}>
                      {feature}
                    </motion.li>
                  )
                )}
              </motion.ul>
            </motion.div>
          )}

          {project.technicalDetails.infrastructure && (
            <motion.div
              className="bg-white/5 p-6 rounded-lg"
              variants={fadeInUp}
              whileHover={scaleOnHover.hover}
              transition={scaleOnHover.transition}
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Infrastructure
              </h3>
              <div className="space-y-2">
                <p className="text-gray-300">
                  <span className="text-pink-500">Hosting:</span>{" "}
                  {project.technicalDetails.infrastructure.hosting}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">Deployment:</span>{" "}
                  {project.technicalDetails.infrastructure.deployment}
                </p>
                <p className="text-gray-300">
                  <span className="text-pink-500">Monitoring:</span>{" "}
                  {project.technicalDetails.infrastructure.monitoring}
                </p>
              </div>
              <h4 className="text-lg font-semibold text-white mt-4 mb-2">
                Notable Features
              </h4>
              <motion.ul
                className="list-disc list-inside text-gray-300 space-y-1"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {project.technicalDetails.infrastructure.notableFeatures.map(
                  (feature, index) => (
                    <motion.li key={index} variants={fadeInUp}>
                      {feature}
                    </motion.li>
                  )
                )}
              </motion.ul>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </>
  );
};
